import { Dashboard } from '../../models/Dashboard'
import { Item } from '../../models/Item'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { getViewTitleFromView } from '../../utils/dashboard'
import { plural } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { getTransactionDocument } from '../../utils/transaction'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import Tooltip from '../app/design/Tooltip'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    items: RecordDataMap[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const getItemType = (tranType?: string) => {
    if (tranType === 'charge') return 'Subscription'
    if (tranType === 'booking') return 'Service'
    return tranType
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.items.map((i) => RecordFactory.getInstance().loadItem(i.id!))
    )
}

function render({
    base: {
        props: { items, dashboard, view, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} purchased`,
                        value: items.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Item</TableHead>
                        <TableHead className="font-normal">Item type</TableHead>
                        <TableHead className="text-right font-normal">Amount</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((i) => {
                        const item = i.valueForRecordKey<Item>('item', Item)
                        const tranType = i.valueForKey('trantype')
                        const tranDoc = getTransactionDocument(i)
                        const amount = i.valueForCurrencyKey('amount')
                        const client = tranDoc?.client

                        return (
                            <ClickableTableRow
                                key={i.id}
                                href={`/${plural(tranType ?? '')}/${tranDoc?.id}`}
                            >
                                <TableCell>
                                    {!client?.name && tranType === 'charge' ? (
                                        <ClientTableCell user={new User({ name: 'Anonymous' })} />
                                    ) : (
                                        <ClientTableCell
                                            user={client}
                                            subaccount={tranDoc?.subaccount}
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-3">
                                        {item?.image?.uri ? (
                                            <img
                                                src={item.image.uri}
                                                alt={item.name}
                                                className="size-6 rounded-full"
                                            />
                                        ) : (
                                            <Icon
                                                icon="shoppingCart.small"
                                                background="round"
                                                className="size-6"
                                            />
                                        )}
                                        <Tooltip label={item?.name} wrap position={'top'}>
                                            <Paragraph
                                                size="sm"
                                                className="max-w-[120px] truncate lg:max-w-[600px]"
                                            >
                                                {item?.name}
                                            </Paragraph>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <Paragraph size="sm" className="capitalize">
                                        {getItemType(tranType)}
                                    </Paragraph>
                                </TableCell>
                                <TableCell>
                                    <Paragraph size="sm" className="text-right">
                                        {currencyFormat(amount, true)}
                                    </Paragraph>
                                </TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {items.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const ItemsDashboard = usePSComponent(render, viewWillAppear)
